import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { storage } from '../utils/localStorage';

interface PermissionsContextType {
    permissions: string[];
    hasPermission: (permission: string) => boolean;
    hasAnyPermission: (permissions: string[]) => boolean;
    hasAllPermissions: (permissions: string[]) => boolean;
    refreshPermissions: () => void;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

// Helper function to parse JWT token
const parseJWT = (token: string): any => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (e) {
        return null;
    }
};

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [permissions, setPermissions] = useState<string[]>([]);

    const loadPermissions = useCallback(() => {
        const token = storage.getItem('token');
        if (!token) {
            setPermissions([]);
            return;
        }

        const decoded = parseJWT(token);

        if (decoded && decoded.user) {
            if (Array.isArray(decoded.user.permissions)) {
                setPermissions(decoded.user.permissions);
            } else {
                setPermissions([]);
            }
        } else {
            setPermissions([]);
        }
    }, []);

    useEffect(() => {
        loadPermissions();

        // Listen for storage changes (token updates from login)
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'flowy_token') {
                loadPermissions();
            }
        };

        window.addEventListener('storage', handleStorageChange);

        // Also listen for custom event when login happens in same tab
        const handleTokenChange = () => {
            loadPermissions();
        };

        window.addEventListener('tokenChanged', handleTokenChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('tokenChanged', handleTokenChange);
        };
    }, [loadPermissions]);

    const hasPermission = useCallback((permission: string): boolean => {
        return permissions.includes(permission);
    }, [permissions]);

    const hasAnyPermission = useCallback((perms: string[]): boolean => {
        return perms.some(p => permissions.includes(p));
    }, [permissions]);

    const hasAllPermissions = useCallback((perms: string[]): boolean => {
        return perms.every(p => permissions.includes(p));
    }, [permissions]);

    const refreshPermissions = useCallback(() => {
        loadPermissions();
    }, [loadPermissions]);

    return (
        <PermissionsContext.Provider value={{
            permissions,
            hasPermission,
            hasAnyPermission,
            hasAllPermissions,
            refreshPermissions
        }}>
            {children}
        </PermissionsContext.Provider>
    );
};

export const usePermissions = () => {
    const context = useContext(PermissionsContext);
    if (!context) {
        throw new Error('usePermissions must be used within PermissionsProvider');
    }
    return context;
};
